import clsx from 'clsx'
import { useEffect } from 'react'
import { useState } from 'react'
import Confetti from 'react-confetti'
import { twMerge as twm } from 'tailwind-merge'

import { Icon, IconNameAndCategoryString } from '../Icon'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import { Clickable } from '../common/Clickable'

type Props = {
    title?: string
    subtitle?: string
    links?: ({
        id: string
        title: string
        subtitle?: string
        icon: IconNameAndCategoryString
    } & ({ to: string } | { onClick: () => void }))[]
    showLinksAsSingleColumn?: boolean
    showConfetti?: boolean
}

export const SuccessView = ({
    title = 'Success',
    subtitle,
    links = [],
    showLinksAsSingleColumn = false,
    showConfetti = false,
}: Props) => {
    const [recycleConfetti, setRecycleConfetti] = useState(showConfetti)

    useEffect(() => {
        setTimeout(() => {
            setRecycleConfetti(false)
        }, 3000)
    }, [showConfetti])

    return (
        <Card className="w-[648px] p-0">
            {showConfetti && <Confetti recycle={recycleConfetti} numberOfPieces={200} />}

            <img src="/workflow-success-banner.svg" className="w-full" />

            <div className="flex flex-col items-stretch justify-start gap-y-8 p-8">
                <div className="flex flex-col items-stretch justify-start gap-y-4">
                    <Heading size="xl" className="text-center">
                        {title}
                    </Heading>
                    {subtitle && (
                        <Label className="text-center leading-6 font-normal text-gray-600">
                            {subtitle}
                        </Label>
                    )}
                </div>

                {links.length > 0 && (
                    <div
                        className={clsx(
                            'mt-2 grid gap-3',
                            showLinksAsSingleColumn ? 'grid-cols-1' : 'grid-cols-2'
                        )}
                        style={{ gridTemplateRows: `repeat(${Math.ceil(links.length / 2)}, 1fr)` }}
                    >
                        {links.map((link) => {
                            const linkBody = (
                                <>
                                    <div className="row-span-2 flex h-full items-center justify-center pr-4">
                                        <Icon icon={link.icon} background="round" />
                                    </div>
                                    <div className="flex shrink grow flex-col items-start justify-center gap-y-1">
                                        <Label
                                            className={twm(
                                                'pointer-events-none text-left',
                                                link.subtitle === undefined
                                                    ? 'row-span-2'
                                                    : 'flex items-end justify-start'
                                            )}
                                        >
                                            {link.title}
                                        </Label>
                                        {link.subtitle && (
                                            <Label
                                                size="sm"
                                                className="pointer-events-none text-left font-normal text-gray-600"
                                            >
                                                {link.subtitle}
                                            </Label>
                                        )}
                                    </div>
                                    <div className="row-span-2 flex h-full items-center justify-center">
                                        <Icon icon="chevronRight.large" className="text-gray-500" />
                                    </div>
                                </>
                            )

                            if ('to' in link) {
                                return (
                                    <Clickable
                                        key={link.id}
                                        href={link.to}
                                        className="flex cursor-pointer items-center justify-start rounded-lg border border-gray-300 p-4 hover:bg-black/4"
                                    >
                                        {linkBody}
                                    </Clickable>
                                )
                            }

                            return (
                                <Clickable
                                    key={link.id}
                                    onClick={link.onClick}
                                    className="flex cursor-pointer items-center justify-start rounded-lg border border-gray-300 p-4 hover:bg-black/4"
                                >
                                    {linkBody}
                                </Clickable>
                            )
                        })}
                    </div>
                )}
            </div>
        </Card>
    )
}
